import { motion } from 'framer-motion'
import { useState } from 'react'
import { Pencil } from 'lucide-react'
import type { JobWizardConfig, MachineProfile } from '../../../../types'
import { STOCK_SHAPES, type ShapeId } from '../../../../lib/stock-shapes'

interface SummaryRow {
  label: string
  value: string
  field: string | null // null = not editable
  editType: 'text' | 'machine' | 'tools' | 'none'
}

interface Props {
  config: Partial<JobWizardConfig>
  readyToGenerate: boolean
  onGenerate: () => void
  onEditField?: (field: string, value: string | number) => void
  onEditMachine?: () => void
  onEditTools?: () => void
  profiles?: MachineProfile[]
  isSuggestingTools?: boolean
}

export default function AgentSummaryBlock({
  config, readyToGenerate, onGenerate, onEditField, onEditMachine, onEditTools,
  profiles = [], isSuggestingTools,
}: Props) {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  const rows: SummaryRow[] = []

  if (config.material) rows.push({ label: 'Material', value: config.material, field: 'material', editType: 'text' })
  if (config.stockSize) {
    const s = config.stockSize
    const shapeId = s.shapeId as ShapeId | undefined
    const def = shapeId ? STOCK_SHAPES[shapeId] : undefined
    const value = def && s.dimensions
      ? def.formatSummary(s.dimensions, 'mm')
      : `${s.x} \u00d7 ${s.y} \u00d7 ${s.z} mm`
    rows.push({ label: 'Stock', value, field: 'stockDims', editType: 'text' })
  }
  if (config.machineId) {
    const profile = profiles.find(p => p.id === config.machineId)
    rows.push({ label: 'Machine', value: profile?.name || config.machineId, field: 'machineProfile', editType: 'machine' })
  }
  if (config.operationType) rows.push({ label: 'Operation', value: config.operationType, field: null, editType: 'none' })
  if (config.tools && config.tools.length > 0) {
    rows.push({ label: 'Tools', value: `${config.tools.length} tool${config.tools.length > 1 ? 's' : ''}`, field: 'tools', editType: 'tools' })
  } else if (isSuggestingTools) {
    rows.push({ label: 'Tools', value: 'suggesting...', field: null, editType: 'none' })
  }

  const startEdit = (field: string, currentValue: string) => {
    setEditingField(field)
    setEditValue(currentValue.replace(/ mm$/, '').replace(/ × /g, ', '))
  }

  const submitEdit = (field: string) => {
    if (!editValue.trim() || !onEditField) return
    if (field === 'stockDims') {
      onEditField(field, editValue.trim())
    } else {
      const num = parseFloat(editValue)
      onEditField(field, isNaN(num) ? editValue.trim() : num)
    }
    setEditingField(null)
    setEditValue('')
  }

  const handleRowClick = (row: SummaryRow) => {
    if (row.editType === 'text' && row.field) {
      startEdit(row.field, row.value)
    } else if (row.editType === 'machine' && onEditMachine) {
      onEditMachine()
    } else if (row.editType === 'tools' && onEditTools) {
      onEditTools()
    }
  }

  const isEditable = (row: SummaryRow) => {
    if (row.editType === 'text' && onEditField) return true
    if (row.editType === 'machine' && onEditMachine) return true
    if (row.editType === 'tools' && onEditTools) return true
    return false
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="my-3 rounded-lg border border-cyan-500/20 bg-white/[0.03] overflow-hidden"
    >
      <div className="px-4 py-2 border-b border-cyan-500/10">
        <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
          Job Summary
        </span>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline gap-3">
            <span className="font-mono text-xs text-slate-500 w-20 shrink-0">{row.label}</span>
            {editingField !== null && editingField === row.field ? (
              <form
                onSubmit={e => { e.preventDefault(); submitEdit(row.field!) }}
                className="flex-1 flex gap-1.5"
              >
                <input
                  type="text"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  autoFocus
                  className="flex-1 bg-white/5 border border-cyan-500/30 rounded px-2 py-0.5 font-mono text-xs text-slate-200 focus:border-cyan-400 focus:outline-none"
                />
                <button type="submit" className="font-mono text-xs text-cyan-400 hover:text-cyan-300">
                  Save
                </button>
                <button type="button" onClick={() => setEditingField(null)} className="font-mono text-xs text-slate-500 hover:text-slate-300">
                  Cancel
                </button>
              </form>
            ) : (
              <span className="font-mono text-xs text-slate-300 flex items-center gap-1.5 group">
                {row.value}
                {isEditable(row) && (
                  <button
                    type="button"
                    onClick={() => handleRowClick(row)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    title={`Edit ${row.label.toLowerCase()}`}
                  >
                    <Pencil className="w-2.5 h-2.5 text-slate-500 hover:text-cyan-400" />
                  </button>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
      {readyToGenerate && (
        <div className="px-4 py-3 border-t border-cyan-500/10">
          <button
            type="button"
            onClick={onGenerate}
            className="w-full py-2 rounded-lg font-mono text-xs font-semibold tracking-wider bg-gradient-to-r from-cyan-500 to-blue-500 text-gray-950 hover:from-cyan-400 hover:to-blue-400 transition-all duration-150 shadow-[0_0_16px_rgba(6,182,212,0.15)]"
          >
            Generate G-code
          </button>
        </div>
      )}
    </motion.div>
  )
}
